import React, { useState, type MouseEvent } from 'react';
import { useTranslation } from '@refinedev/core';
import {
  ActionIcon,
  AspectRatio,
  Group,
  Loader,
  Modal,
  Tooltip,
} from '@mantine/core';
import { IconDownload, IconEye } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';
import { generateGetSignedUrl } from 'api/action/s3';
import { useDownloadFromS3 } from 'hooks';
import type { Statement } from 'interfaces';
import type { MRT_Row } from 'mantine-react-table';

interface StatementRowActionsProps {
  row: MRT_Row<Statement>;
}

const BUCKET = `report-files-${import.meta.env.VITE_ACCOUNT_ID}`;

export const StatementRowActions: React.FC<StatementRowActionsProps> = ({
  row,
}) => {
  const { translate } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  const reportPath = row.original.reportPath ?? '';
  const key = encodeURIComponent(reportPath);
  const canPreview = reportPath.toLowerCase().endsWith('.pdf');
  const downloadFile = useDownloadFromS3(BUCKET, key, setDownloading);
  const viewLabel = translate(
    'pages:statements.actions.view',
    'View statement'
  );
  const downloadLabel = translate(
    'pages:statements.actions.download',
    'Download statement'
  );

  const viewStatement = async () => {
    try {
      setViewLoading(true);
      if (!reportPath || !canPreview) return;

      const url = await generateGetSignedUrl({
        bucket: BUCKET,
        key,
      });
      setFileUrl(url);
      setModalOpen(true);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setViewLoading(false);
    }
  };

  const handleView = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void viewStatement();
  };

  const handleDownload = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void downloadFile();
  };

  return (
    <>
      <Group gap="xs" wrap="nowrap">
        <Tooltip label={viewLabel} withArrow>
          <ActionIcon
            variant="light"
            aria-label={viewLabel}
            disabled={!reportPath || !canPreview || viewLoading}
            onClick={handleView}
          >
            {viewLoading ? <Loader size={16} /> : <IconEye size={16} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label={downloadLabel} withArrow>
          <ActionIcon
            variant="light"
            aria-label={downloadLabel}
            disabled={!reportPath || downloading}
            onClick={handleDownload}
          >
            {downloading ? <Loader size={16} /> : <IconDownload size={16} />}
          </ActionIcon>
        </Tooltip>
      </Group>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={translate(
          'pages:statements.actions.previewTitle',
          'Statement preview'
        )}
        size="xl"
        centered
        closeOnEscape
        closeOnClickOutside
      >
        {fileUrl ? (
          <AspectRatio ratio={1} style={{ width: '100%' }}>
            <iframe
              src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title={translate(
                'pages:statements.actions.previewFrameTitle',
                'Statement PDF preview'
              )}
              style={{
                border: 'none',
                padding: '2%',
              }}
            />
          </AspectRatio>
        ) : (
          <Loader />
        )}
      </Modal>
    </>
  );
};
